import 'zone.js';
import 'core-js/proposals/reflect-metadata';

import {bootstrapApplication, Title} from '@angular/platform-browser';

import {ProgressSpinnerDialogService} from './app/shared/services/progress-spinner-dialog.service';
import {ExportService} from './app/shared/services/export.service';
import {ReminderService} from './app/home/services/reminder.service';
import {SettingsService} from './app/settings/services/settings.service';
import {ChartService} from './app/shared/services/chart.service';
import {ReleaseNotesService} from './app/shared/services/release-notes.service';
import {BookmarkService} from './app/shared/services/bookmark.service';
import {importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {MsalService} from '@azure/msal-angular';
import {contactsSourceProvider} from './app/shared/contacts/contacts-source';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import {CoreModule} from './app/core/core.module';
import {provideAnimations} from '@angular/platform-browser/animations';
import {HighchartsChartModule} from 'highcharts-angular';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {MatSidenavModule} from '@angular/material/sidenav';
import {AppComponent} from './app/app.component';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {AUTH_PROVIDERS} from './app/core/auth/auth-config';
import {loadRuntimeAuthConfig, RUNTIME_AUTH_CONFIG} from './app/core/auth/runtime-auth-config';
import {customIconInitProvider} from './app/core/initializers/custom-icons.init';
import {provideRouter, withComponentInputBinding, withRouterConfig} from '@angular/router';
import {APP_ROUTES} from './app/app.routes';
import {firstValueFrom} from 'rxjs';
import {httpInterceptorProviders} from './app/core/http-interceptors';
import {AmplitudeService} from './app/core/services/amplitude.service';
import {provideTransloco} from '@jsverse/transloco';
import {provideTranslocoLocale} from '@jsverse/transloco-locale';
import {TranslocoHttpLoader} from './app/core/i18n/transloco-loader';
import {LocaleService} from './app/core/services/locale.service';
import {AVAILABLE_LANGS, DEFAULT_LANG, LANG_TO_LOCALE} from './app/core/i18n/i18n.config';
import {environment} from './environments/environment';

async function bootstrap() {
  const runtimeAuthConfig = await loadRuntimeAuthConfig();

  await bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      importProvidersFrom(
        CoreModule, HighchartsChartModule, MatSnackBarModule, ScrollingModule,
        MatSidenavModule, MatNativeDateModule, MatSnackBarModule
      ),
      provideRouter(APP_ROUTES, withComponentInputBinding(), withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' })),
      Title,
      ProgressSpinnerDialogService,
      ExportService,
      ReminderService,
      SettingsService,
      ChartService,
      ReleaseNotesService,
      BookmarkService,
      contactsSourceProvider,
      {provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS},
      {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {strict: true, useUtc: true}},
      {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]},
      {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline'}},
      provideAnimations(),
      provideTransloco({
        config: {
          availableLangs: [...AVAILABLE_LANGS],
          defaultLang: DEFAULT_LANG,
          fallbackLang: DEFAULT_LANG,
          reRenderOnLangChange: true,
          prodMode: environment.production,
          missingHandler: {
            logMissingKey: !environment.production,
            useFallbackTranslation: true,
          },
        },
        loader: TranslocoHttpLoader,
      }),
      provideTranslocoLocale({
        langToLocaleMapping: LANG_TO_LOCALE,
      }),
      ...httpInterceptorProviders,
      AUTH_PROVIDERS,
      {provide: RUNTIME_AUTH_CONFIG, useValue: runtimeAuthConfig},
      customIconInitProvider,
      provideAppInitializer(async () => {
        const msalService = inject(MsalService);
        await firstValueFrom(msalService.initialize());
      }),
      provideAppInitializer(() => {
        return inject(LocaleService).init();
      }),
      provideAppInitializer(() => {
        inject(AmplitudeService).init();
      }),
    ]
  });
}

void bootstrap();
